import { usersApi } from '@/api';
import { BasicResponse } from '@/api/common/common.types.ts';
import { queryKeys } from '@/api/query-keys.ts';
import {
  AdminChangePassword,
  ChangePassword,
  SalesPersonCandidate,
  UpdateUserAccessRequest,
  UserDetail,
} from '@/api/user/user.types.ts';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const useChangePassword = (
  options?: UseMutationOptions<BasicResponse, AxiosError, ChangePassword>,
) => {
  return useMutation<BasicResponse, AxiosError, ChangePassword>({
    mutationFn: (changePassword) =>
      usersApi.changePassword(changePassword).then((res) => res.data),
    ...options,
  });
};

export const useAdminChangePassword = (
  options?: UseMutationOptions<BasicResponse, AxiosError, AdminChangePassword>,
) => {
  return useMutation<BasicResponse, AxiosError, AdminChangePassword>({
    mutationFn: (changePassword) =>
      usersApi.adminChangePassword(changePassword).then((res) => res.data),
    ...options,
  });
};

export const useGetAllUsers = (options?: UseQueryOptions<UserDetail[]>) => {
  return useQuery<UserDetail[]>({
    queryKey: queryKeys.users.all,
    queryFn: () => usersApi.getAllUsers().then((res) => res.data),
    ...options,
  });
};

/**
 * SAP-synced sales persons, used to populate the user-access editor's
 * picker. The list is small (a few dozen rows) so we fetch the full set
 * once and filter client-side.
 */
export const useSalesPersonCandidates = (
  options?: UseQueryOptions<SalesPersonCandidate[]>,
) =>
  useQuery<SalesPersonCandidate[]>({
    queryKey: ['users', 'sales-person-candidates'],
    queryFn: () =>
      usersApi.getSalesPersonCandidates().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useUpdateUserAccess = (
  options?: UseMutationOptions<
    UserDetail,
    AxiosError,
    { userId: string; body: UpdateUserAccessRequest }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    UserDetail,
    AxiosError,
    { userId: string; body: UpdateUserAccessRequest }
  >({
    mutationFn: ({ userId, body }) =>
      usersApi.updateUserAccess(userId, body).then((res) => res.data),
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      options?.onSuccess?.(...args);
    },
  });
};
